import {getNodeId} from '../utils/get-node-id';
import {findNodeById} from '../utils/find-nodes-by-id';
import {editorState} from '@app/editor/state/editor-store';
import {MessageDescriptor} from '@common/i18n/message-descriptor';
import {nanoid} from 'nanoid';

export abstract class BaseMutation {
  uniqueId: string;
  createdAt: number;
  abstract displayName: MessageDescriptor;

  protected nodeId: string | null;
  protected pageId: string | null;

  protected abstract changes: {new: unknown; old: unknown};

  protected abstract executeMutation(doc: Document): boolean;
  protected abstract undoMutation(doc: Document): boolean;
  protected onBeforeExecute() {
    //
  }

  protected get pageDoc(): Document | null {
    return this.getPageDoc();
  }

  protected get previewDoc(): Document | null {
    return this.getPreviewDoc();
  }

  protected constructor(protected nodeOrId: HTMLElement | string) {
    this.nodeId = typeof nodeOrId === 'string' ? nodeOrId : getNodeId(nodeOrId);
    this.pageId = editorState().activePageId;
    this.uniqueId = nanoid(10);
    this.createdAt = Date.now();
  }

  private getPageDoc(): Document | null {
    const project = editorState().project;
    if (!project || !this.pageId) return null;
    const page = project.pages.find(p => p.id === this.pageId);
    return page?.doc ?? null;
  }

  private getPreviewDoc(): Document | null {
    if (editorState().activePageId !== this.pageId) return null;
    return editorState().getEditorDoc();
  }

  init(): this {
    this.onBeforeExecute();
    return this;
  }

  execute(): boolean {
    const pageDoc = this.pageDoc;
    const previewDoc = this.previewDoc;
    if (!this.nodeId || !pageDoc) {
      return false;
    }

    const docs = previewDoc ? [pageDoc, previewDoc] : [pageDoc];
    return docs
      .map(doc => this.executeMutation(doc))
      .some(executed => executed);
  }

  undo(): boolean {
    const pageDoc = this.pageDoc;
    const previewDoc = this.previewDoc;
    if (!this.nodeId || !pageDoc) {
      return false;
    }

    const docs = previewDoc ? [pageDoc, previewDoc] : [pageDoc];
    return docs
      .map(doc => this.undoMutation(doc))
      .every(executed => executed);
  }

  redo(): boolean {
    if (!this.nodeId || !this.pageDoc) {
      return false;
    }
    return this.execute();
  }

  getNodeId() {
    return this.nodeId;
  }

  getInitialValue() {
    return this.changes.old;
  }

  overrideInitialValue(value: unknown) {
    this.changes.old = value;
  }

  protected findEl(doc: Document, elId?: string | null): HTMLElement | null {
    elId = elId || this.nodeId;
    return elId ? findNodeById(elId, doc) : null;
  }
}
